/**
 * ControlsRow - Controls for LiveDashboardV2
 * Add/Reduce Time, Countdown timer with duration info, Download CSV
 */
import React, { useState, useEffect, useRef } from 'react'
import { Button } from 'react-bootstrap'
import { FiDownload } from 'react-icons/fi'
import { CountdownDisplay, RefreshButton } from './RefreshContext'
import EditableTimeCell from './EditableTimeCell'
import styles from '../../LabsV2.module.css'

/**
 * Convert "HH:MM:SS" duration string to human-readable format
 * e.g. "01:30:00" → "1h 30m", "00:45:00" → "45m", "00:00:00" → "0m"
 */
const formatDuration = (durationStr) => {
  if (!durationStr) return '—'
  const clean = durationStr.replace(/^-/, '')
  const parts = clean.split(':')
  if (parts.length !== 3) return durationStr
  const hours = parseInt(parts[0], 10)
  const minutes = parseInt(parts[1], 10)
  if (hours > 0 && minutes > 0) return `${hours}h ${minutes}m`
  if (hours > 0) return `${hours}h`
  return `${minutes}m`
}

/**
 * Parse "HH:MM:SS" to total minutes (signed)
 */
const toTotalMinutes = (durationStr) => {
  if (!durationStr) return 0
  const negative = durationStr.startsWith('-')
  const clean = negative ? durationStr.slice(1) : durationStr
  const parts = clean.split(':')
  if (parts.length !== 3) return 0
  const total = parseInt(parts[0], 10) * 60 + parseInt(parts[1], 10)
  return negative ? -total : total
}

/**
 * Format milliseconds to HH:MM:SS or MM:SS countdown string
 */
const formatCountdown = (ms) => {
  if (ms <= 0) return '00:00'
  const totalSeconds = Math.floor(ms / 1000)
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60
  const pad = (n) => String(n).padStart(2, '0')
  if (hours > 0) return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
  return `${pad(minutes)}:${pad(seconds)}`
}

const ControlsRow = ({
  examId,
  onTimeModified,
  onDownloadCSV,
  isLoading,
  isWebSocketConnected,
  labDuration,
  labAdditionalTime,
  timeLeft
}) => {
  const [remaining, setRemaining] = useState(timeLeft)
  const intervalRef = useRef(null)

  // Reset countdown when timeLeft prop changes (after API refresh)
  useEffect(() => {
    if (timeLeft != null) {
      setRemaining(timeLeft)
    }
  }, [timeLeft])

  // Tick down every second
  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setRemaining(prev => (prev != null && prev > 0) ? prev - 1000 : 0)
    }, 1000)
    return () => clearInterval(intervalRef.current)
  }, [])

  const additionalMinutes = toTotalMinutes(labAdditionalTime)
  const examEnded = remaining != null && remaining <= 0

  const additionalLabel = 'Overall Adjusted Time'

  const additionalTimeDisplay = additionalMinutes > 0
    ? `+${formatDuration(labAdditionalTime)}`
    : additionalMinutes < 0
      ? `${formatDuration(labAdditionalTime)}`
      : '0m'

  const additionalTimeClass = additionalMinutes > 0
    ? styles.controlsDurationPositive
    : additionalMinutes < 0
      ? styles.controlsDurationNegative
      : ''

  return (
    <div className={styles.dashboardControlsRow}>
      {/* Add/Reduce Time for All */}
      <div className={styles.controlsTimeSection}>
        <span className={styles.controlsLabel}>Add / Reduce Time (minutes):</span>
        <EditableTimeCell
          modifyAll={true}
          examId={examId}
          onSuccess={onTimeModified}
        />
      </div>

      {/* Center section — Countdown & Additional/Reduced Time */}
      {labDuration && (
        <div className={styles.controlsCenterSection}>
          <div className={styles.controlsCountdown}>
            <span className={`${styles.controlsCountdownValue} ${examEnded ? styles.controlsCountdownEnded : ''}`}>
              {remaining != null ? formatCountdown(remaining) : formatDuration(labDuration)}
            </span>
          </div>
          {additionalMinutes !== 0 && (
            <div className={styles.controlsAdditionalRow}>
              <span className={styles.controlsAdditionalText}>
                {additionalLabel}: <span className={additionalTimeClass}>{additionalTimeDisplay}</span>
              </span>
            </div>
          )}
        </div>
      )}

      {/* Right side controls */}
      <div className={styles.controlsRightSection}>
        {/* WebSocket connection status */}
        <div className={styles.wsStatusIndicator}>
          <span
            className={isWebSocketConnected ? styles.wsStatusDotConnected : styles.wsStatusDotDisconnected}
          />
          <span className={isWebSocketConnected ? styles.wsLiveLabel : styles.controlsLabel}>
            {isWebSocketConnected ? 'Live' : 'Polling'}
          </span>
        </div>

        {/* Refresh countdown + button — only shown when not live */}
        {!isWebSocketConnected && (
          <>
            <div className={styles.controlsRefreshSection}>
              <span className={styles.controlsLabel}>Next refresh in:</span>
              <CountdownDisplay />
            </div>

            <RefreshButton
              variant="info"
              className={styles.controlsRefreshBtn}
              disabled={isLoading}
            >
              {isLoading ? 'Refreshing...' : 'Refresh Now'}
            </RefreshButton>
          </>
        )}

        {/* Download CSV button */}
        <Button
          variant="primary"
          className={styles.controlsDownloadBtn}
          onClick={onDownloadCSV}
        >
          <FiDownload size={16} />
          <span>Download as CSV</span>
        </Button>
      </div>
    </div>
  )
}

export default ControlsRow
